import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { CourseDetail, CourseEnrollment } from '../../types/content';
import Button from '../../components/UI/Button';
import CourseFee from '../../components/UI/CourseFee';
import { PageLoading } from '../../components/UI/LoadingOverlay';

const CoursePaymentPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [enrollment, setEnrollment] = useState<CourseEnrollment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!slug) return;
    contentApi.getCourseBySlug(slug).then(async (r) => {
      const loaded = r.data.course;
      if (!loaded) {
        setError('This course is not available.');
        return;
      }
      setCourse(loaded);
      let current = (await contentApi.getCourseEnrollment(loaded.id)).data.enrollment || null;
      if (!current) {
        const enrolled = await contentApi.enrollCourse(loaded.id);
        current = enrolled.data.enrollment || null;
      }
      setEnrollment(current);
      if (current?.isPaid) {
        const topic = current.firstTopicSlug || loaded.modules?.flatMap((m) => m.topics)[0]?.slug;
        navigate(topic ? `/courses/${loaded.slug}/topics/${topic}` : `/courses/${loaded.slug}`, { replace: true });
      }
    }).catch(() => setError('Unable to load payment details.'));
  }, [slug, navigate]);

  const completePayment = async () => {
    if (!course || !enrollment) return;
    setPaying(true);
    setError(null);
    try {
      const response = await contentApi.completeCoursePayment(enrollment.id);
      const next = response.data.enrollment;
      const topic = next?.firstTopicSlug || course.modules?.flatMap((m) => m.topics)[0]?.slug;
      navigate(topic ? `/courses/${course.slug}/topics/${topic}` : `/courses/${course.slug}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Payment could not be completed.');
    } finally {
      setPaying(false);
    }
  };

  if (error && !course) return <div className="max-w-3xl mx-auto px-4 py-16 text-gray-600">{error}</div>;
  if (!course) return <PageLoading label="Loading payment details…" />;

  return (
    <div className="max-w-xl mx-auto px-4 py-16">
      <p className="text-sm text-primary-600 font-medium">Payment placeholder</p>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Complete payment</h1>
      <p className="mt-3 text-gray-600">
        A payment gateway will be connected later. For now this page confirms the amount and unlocks the course after you continue.
      </p>
      <div className="mt-8 surface-card p-6 space-y-4">
        <div>
          <p className="text-sm text-gray-500">Course</p>
          <p className="font-semibold text-gray-900">{course.title}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Amount due</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{Number(enrollment?.payable ?? 0).toLocaleString('en-IN')}
          </p>
          <div className="mt-2 space-y-1 text-sm text-gray-600">
            <p>Course: <CourseFee fee={Number(enrollment?.courseFeePortion ?? enrollment?.price ?? course.price ?? 0)} discount={Number(enrollment?.courseFeePortion != null ? 0 : (enrollment?.discountPercent ?? course.discountPercent ?? 0))} /></p>
            {(enrollment?.interviewPackageCount || 0) > 0 && (
              <p>
                {enrollment?.interviewPackageCount} review session{(enrollment?.interviewPackageCount || 0) === 1 ? '' : 's'}: ₹
                {Number(enrollment?.interviewPackageFee || 0).toLocaleString('en-IN')}
              </p>
            )}
          </div>
        </div>
        {error && <p className="text-sm text-error-600">{error}</p>}
        <Button size="lg" fullWidth onClick={completePayment} disabled={paying || !enrollment}>
          {paying ? 'Confirming…' : 'Pay and start learning'}
        </Button>
        <Link to={`/courses/${course.slug}`} className="block text-center text-sm text-gray-500 hover:text-primary-600">
          Back to course
        </Link>
      </div>
    </div>
  );
};

export default CoursePaymentPage;
